import { Show } from '../../../../components/Show';
import { FieldType, getRadioOptions } from '../../../parsing/parser';
import type { FormFieldDef } from '../../../types';
import { FieldControlLabel } from '../FieldControlLabel';
import { FieldControlText } from '../FieldControlText';
import { determineIfRequired } from '../helpers/form-layout-utils';
import { useGetShowCondition } from '../hooks/useGetShowCondition';
import { useErrorMessageForField } from '../hooks/useErrorMessageForField';
import { LegacyRadioGroupingContainer } from './LegacyFieldControl/LegacyRadioGrouping';
import { LegacyMultiSelectContainer } from './LegacyFieldControl/LegacyMultiSelect';

type FieldControlProps = {
  name: string;
  value: any;
  onChange: (value: any) => void;
  meta: FormFieldDef;
  error?: string;
  conditions?: FormFieldDef['conditions'];
  disabled?: boolean;
  confidenceScore?: number;
  showConfidence?: boolean;
  formId: string;
  // RAG props
  enableRag?: boolean;
  submissionId?: string;
};

export const LegacyFieldControl = (props: FieldControlProps) => {
  const errorMessage = useErrorMessageForField(props.meta.id);
  const isShowing = useGetShowCondition(props.conditions);

  const commonProps = {
    name: props.name,
    value: props.value,
    onChange: props.onChange,
    meta: props.meta,
  };

  // Common RAG props to pass to all FieldControlLabel instances
  const ragLabelProps = {
    enableRag: props.enableRag,
    submissionId: props.submissionId,
    fieldType: props.meta.type,
    fieldMeta: props.meta,
  };

  const hasLabel = props.meta.text != null;
  const isRequired = determineIfRequired(
    props.meta.type,
    props.meta.schema,
    props.meta.required
  );

  if (
    props.meta.type == FieldType.Radio ||
    props.meta.type == FieldType.SingleSelect
  ) {
    return (
      <Show when={isShowing}>
        <LegacyRadioGroupingContainer
          {...commonProps}
          value={props.value}
          options={getRadioOptions(props.meta.valueOptions)}
          error={errorMessage}
          disabled={props.disabled}
          labelEl={
            <FieldControlLabel
              hasLabel={hasLabel}
              isRequired={isRequired}
              text={props.meta.text}
              confidenceScore={props.confidenceScore}
              showConfidence={props.showConfidence}
              fieldId={props.meta.id}
              formId={props.formId}
              sx={{
                padding: '1.5rem 0 0.5rem 0',
              }}
              {...ragLabelProps}
            />
          }
        />
      </Show>
    );
  }

  if (props.meta.type == FieldType.MultiSelect) {
    return (
      <Show when={isShowing}>
        <LegacyMultiSelectContainer
          {...commonProps}
          value={props.value}
          options={getRadioOptions(props.meta.valueOptions)}
          error={errorMessage}
          disabled={props.disabled}
          labelEl={
            <FieldControlLabel
              hasLabel={hasLabel}
              isRequired={isRequired}
              text={props.meta.text}
              confidenceScore={props.confidenceScore}
              showConfidence={props.showConfidence}
              fieldId={props.meta.id}
              formId={props.formId}
              sx={{
                padding: '1.5rem 0 0.5rem 0',
              }}
              {...ragLabelProps}
            />
          }
        />
      </Show>
    );
  }

  if (props.meta.type == FieldType.Text) {
    return (
      <Show when={isShowing}>
        <FieldControlText
          isRequired={isRequired}
          hasLabel={hasLabel}
          commonProps={commonProps}
          errorMessage={errorMessage}
          meta={props.meta}
          disabled={props.disabled}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />
      </Show>
    );
  }

  return null;
};
